"use client"

import { Analytics } from "@vercel/analytics/next"
import { SpeedInsights } from "@vercel/speed-insights/next"
import Link from "next/link"
import { usePathname } from "next/navigation"
import { useEffect, useSyncExternalStore, type ReactNode } from "react"
import { ChatwootWidget } from "@/components/chatwoot-widget"
import { WhatsAppSupportFloatingButton } from "@/components/whatsapp-support-floating-button"
import { GoogleTagManagerNoScript, GoogleTagManagerScript } from "@/components/google-tag-manager"
import { isChatwootPublicWidgetEnabled, warnIfChatwootPublicConfigIncomplete } from "@/lib/chatwoot/public-widget"
import { initPosthogClientOnce } from "@/lib/posthog-client-init"
import { phOptInCapturing, phOptOutCapturing } from "@/lib/posthog-browser"

/**
 * Lógica de consentimento inlined de propósito: importar `lib/cookie-consent.ts` daqui
 * quebra o factory do Turbopack em dev (“module factory is not available”). Manter em
 * sync com `lib/cookie-consent.ts` e `instrumentation-client.ts`.
 */
const CONSENT_COOKIE_NAME = "astra_cookie_consent"
const CONSENT_ACCEPTED = "accepted"
const CONSENT_REJECTED = "rejected"
const CONSENT_MAX_AGE_SECONDS = 60 * 60 * 24 * 180
const OPEN_COOKIE_BANNER_EVENT = "astra-open-cookie-banner"

type ConsentChoice = typeof CONSENT_ACCEPTED | typeof CONSENT_REJECTED

function readConsentFromDocument(): ConsentChoice | null {
  if (typeof document === "undefined") return null
  const match = document.cookie.match(new RegExp(`(?:^|; )${CONSENT_COOKIE_NAME}=([^;]+)`))
  const value = match?.[1]
  if (value === CONSENT_ACCEPTED || value === CONSENT_REJECTED) return value
  return null
}

/** Opt-out: tracking ativo até recusa explícita. */
function isOptionalTrackingAllowed(consent: ConsentChoice | null): boolean {
  return consent !== CONSENT_REJECTED
}

function persistConsent(choice: ConsentChoice) {
  document.cookie = `${CONSENT_COOKIE_NAME}=${choice}; Max-Age=${CONSENT_MAX_AGE_SECONDS}; Path=/; SameSite=Lax`
}

function clearConsent() {
  document.cookie = `${CONSENT_COOKIE_NAME}=; Max-Age=0; Path=/; SameSite=Lax`
}

function subscribeConsent(onStoreChange: () => void) {
  if (typeof window === "undefined") return () => {}
  const handler = () => onStoreChange()
  window.addEventListener("astra-cookie-consent-updated", handler)
  return () => window.removeEventListener("astra-cookie-consent-updated", handler)
}

function getConsentSnapshot(): ConsentChoice | null {
  return readConsentFromDocument()
}

/** Consent Mode v2 — inlined (sync com lib/gtm/consent-mode-snippet.ts). */
function pushGtmConsentUpdate(trackingAllowed: boolean): void {
  if (typeof window === "undefined") return
  const state = trackingAllowed ? "granted" : "denied"
  const dataLayer = (window as Window & { dataLayer?: Record<string, unknown>[] }).dataLayer
  const layer = dataLayer ?? []
  ;(window as Window & { dataLayer?: Record<string, unknown>[] }).dataLayer = layer
  const gtag = (window as Window & { gtag?: (...args: unknown[]) => void }).gtag
  if (typeof gtag === "function") {
    gtag("consent", "update", {
      analytics_storage: state,
      ad_storage: state,
      ad_user_data: state,
      ad_personalization: state,
    })
  }
  layer.push({
    event: "consent_update",
    analytics_storage: state,
    ad_storage: state,
    ad_user_data: state,
    ad_personalization: state,
  })
}

function PrivacyPolicyLink({
  children,
  "aria-label": ariaLabel,
}: {
  children: ReactNode
  "aria-label"?: string
}) {
  return (
    <Link
      href="/privacidade"
      target="_blank"
      rel="noreferrer"
      aria-label={ariaLabel}
      className="underline underline-offset-2"
    >
      {children}
    </Link>
  )
}

function CookieConsentBanner({ onChoice }: { onChoice: (choice: ConsentChoice) => void }) {
  return (
    <div
      data-cookie-consent-banner
      className="fixed inset-x-0 bottom-0 z-[90] border-t border-border bg-background/95 px-3 py-2.5 backdrop-blur sm:p-4"
    >
      <div className="mx-auto flex max-w-5xl items-center justify-between gap-3">
        <p className="min-w-0 text-sm leading-snug text-foreground/80 sm:hidden">
          Usamos cookies.{" "}
          <PrivacyPolicyLink aria-label="Política de Privacidade">Saiba mais</PrivacyPolicyLink>
        </p>
        <p className="hidden min-w-0 text-sm text-foreground/80 sm:block">
          Usamos cookies necessários para o site funcionar e cookies não necessários para análises e melhoria da
          experiência. Você pode aceitar ou recusar os cookies não necessários. Saiba mais na{" "}
          <PrivacyPolicyLink>Política de Privacidade</PrivacyPolicyLink>.
        </p>
        <div className="flex shrink-0 gap-2">
          <button
            type="button"
            onClick={() => onChoice(CONSENT_REJECTED)}
            className="rounded-md border border-input px-3 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent sm:px-4"
          >
            Recusar
          </button>
          <button
            type="button"
            onClick={() => onChoice(CONSENT_ACCEPTED)}
            className="rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90 sm:px-4"
          >
            Aceitar
          </button>
        </div>
      </div>
    </div>
  )
}

export function CookieConsentManager() {
  const pathname = usePathname()
  const consent = useSyncExternalStore(subscribeConsent, getConsentSnapshot, () => null)
  const isClient = useSyncExternalStore(
    () => () => {},
    () => true,
    () => false,
  )

  const trackingAllowed = isOptionalTrackingAllowed(consent)

  useEffect(() => {
    if (trackingAllowed) {
      initPosthogClientOnce()
      phOptInCapturing()
    } else {
      phOptOutCapturing()
    }
    pushGtmConsentUpdate(trackingAllowed)
  }, [trackingAllowed])

  const handleChoice = (choice: ConsentChoice) => {
    persistConsent(choice)
    pushGtmConsentUpdate(isOptionalTrackingAllowed(choice))
    window.dispatchEvent(new Event("astra-cookie-consent-updated"))
  }

  const handleReopenBanner = () => {
    clearConsent()
    window.dispatchEvent(new Event("astra-cookie-consent-updated"))
  }

  useEffect(() => {
    const openFromEvent = () => {
      handleReopenBanner()
    }
    window.addEventListener(OPEN_COOKIE_BANNER_EVENT, openFromEvent)
    return () => {
      window.removeEventListener(OPEN_COOKIE_BANNER_EVENT, openFromEvent)
    }
  }, [])

  useEffect(() => {
    warnIfChatwootPublicConfigIncomplete()
  }, [])

  if (pathname?.startsWith("/render")) {
    return null
  }

  return (
    <>
      {trackingAllowed ? (
        <>
          <GoogleTagManagerScript />
          <GoogleTagManagerNoScript />
          <Analytics />
          {pathname?.startsWith("/admin") || pathname?.startsWith("/painel") ? null : (
            <SpeedInsights sampleRate={0.25} />
          )}
        </>
      ) : null}
      {isChatwootPublicWidgetEnabled() ? (
        <ChatwootWidget />
      ) : (
        <WhatsAppSupportFloatingButton />
      )}
      {isClient && consent === null ? <CookieConsentBanner onChoice={handleChoice} /> : null}
    </>
  )
}
